import {FC, useContext, useEffect} from 'react';
import {TableToolbar} from './TableToolbar';
import {Paper, Table, TableContainer, TablePagination} from '@mui/material';
import {TableHeadContainer} from './TableHeadContainer';
import {TableBodyContainer} from './TableBody/TableBodyContainer';
import {useRecoilState, useRecoilStateLoadable, useRecoilValue, useSetRecoilState} from 'recoil';
import {useDebouncePageable} from '../../../hooks/useDebouncePageable';
import {EventBusContext} from '../../../providers/EventBus';
import {IPageableTable} from '../../../shared/types/pageable.types';
import {deviceModelsQuery} from '../../../store/deviceModels/selectors';
import {deviceModelsPageable, deviceModelsQueryTrigger, deviceModelsState} from '../../../store/deviceModels/atoms';

export const DeviceModelsList: FC = () => {
  const triggerQuery = useSetRecoilState(deviceModelsQueryTrigger);
  const {totalElements} = useRecoilValue(deviceModelsState);
  const [pageable, setPageable] = useRecoilState(deviceModelsPageable);
  const {
    pageableToDebounce: {page, size},
    setPageableToDebounce,
  } = useDebouncePageable<IPageableTable>(pageable, setPageable);
  const {bus} = useContext(EventBusContext);

  const [{state, contents}, setDeviceModels] = useRecoilStateLoadable(deviceModelsQuery);

  const handleChangePage = (event: unknown, newPage: number) => {
    setPageableToDebounce({page: newPage, size});
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPageableToDebounce({page: 0, size: parseInt(event.target.value, 10)});
    localStorage.setItem('deviceModelsPerPage', event.target.value);
  };

  useEffect(() => {
    if (state === 'hasValue') setDeviceModels(contents);

    if (state === 'hasError')
      bus?.emit('error', [
        contents,
        {
          toastId: 'deviceModels',
        },
      ]);

    return () => {
      state !== 'loading' && triggerQuery((v) => v + 1);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state]);

  return (
    <Paper>
      <TableToolbar loadingState={state} />
      <TableContainer>
        <Table size="small">
          <TableHeadContainer />
          <TableBodyContainer rowsPerPage={size} />
        </Table>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={totalElements}
          page={page}
          rowsPerPage={size}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </TableContainer>
    </Paper>
  );
};
